import { useCallback, useMemo } from "react";

import { useTicker } from "@/providers/TickerProvider";

/**
 * Custom hook for converting currency amounts to base currency
 * Uses real-time ticker prices for conversion
 */
const useBaseCurrencyConverter = () => {
  const { baseCurrency, convertToBaseCurrency, isPricesLoading } = useTicker();

  /**
   * Convert a single currency amount to base currency
   */
  const convert = useCallback(
    (amount: number, currency: string): number => {
      return convertToBaseCurrency(amount, currency);
    },
    [convertToBaseCurrency]
  );

  /**
   * Convert multiple currency amounts and sum them
   * Useful for calculating total portfolio value
   */
  const convertAndSum = useCallback(
    (amounts: Array<{ amount: number; currency: string }>): number => {
      return amounts.reduce((total, { amount, currency }) => {
        return total + convertToBaseCurrency(amount, currency);
      }, 0);
    },
    [convertToBaseCurrency]
  );

  /**
   * Check if a currency is the base currency
   */
  const isBaseCurrency = useCallback(
    (currency: string): boolean => {
      return currency === baseCurrency;
    },
    [baseCurrency]
  );

  return useMemo(
    () => ({
      baseCurrency,
      convert,
      convertAndSum,
      isBaseCurrency,
      isPricesLoading,
    }),
    [baseCurrency, convert, convertAndSum, isBaseCurrency, isPricesLoading]
  );
};

export default useBaseCurrencyConverter;
